import { Component } from "react";
import { QESAlert } from "../shared/qes-standards";

interface MaintenanceAlertProps {
  maintenanceTime: string;
}

interface MaintenanceAlertState {
  // the text that informs the user when maintenance will be conducted;
  // if a countdown is not present, the default verbiage is:
  // "Warning! We will be conducting maintenance momentarily."
  countdownText: string;
  // the time remaining until maintenance in milliseconds
  timeRemainingInMilliseconds: number;
}

class MaintenanceAlert extends Component<MaintenanceAlertProps, MaintenanceAlertState> {
  private timer: NodeJS.Timeout;

  constructor(props: MaintenanceAlertProps) {
    super(props);

    this.state = {
      countdownText: "momentarily",
      timeRemainingInMilliseconds: null,
    };
  }

  public async componentDidMount() {
    await this.calculateTimeRemaining().then(() => {
      this.timer = setInterval(() => {
        this.decrementTimeRemaining();
      }, 1000);
    });
  }

  public render() {
    return (
      <QESAlert classNames="mb0 mt-2" type="danger">
        <b>Warning!</b> We will be conducting maintenance {this.state.countdownText}.
        <i className="fa fa-wrench mr-1 float-right" />
      </QESAlert>
    );
  }

  private calculateCountdown = (timeRemainingInMilliseconds: number) => {
    const days: number = Math.floor(timeRemainingInMilliseconds / (1000 * 60 * 60 * 24));
    const hours: number = Math.floor((timeRemainingInMilliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes: number = Math.floor((timeRemainingInMilliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds: number = Math.floor((timeRemainingInMilliseconds % (1000 * 60)) / 1000);

    let countdownText = "in";

    if (days > 0) {
      if (days === 1) {
        countdownText += " " + days + " day";
      } else {
        countdownText += " " + days + " days";
      }
    }
    if (hours > 0) {
      if (hours === 1) {
        countdownText += " " + hours + " hour";
      } else {
        countdownText += " " + hours + " hours";
      }
    }
    if (minutes > 0) {
      if (minutes === 1) {
        countdownText += " " + minutes + " minute";
      } else {
        countdownText += " " + minutes + " minutes";
      }
    }
    if (seconds > 0) {
      if (seconds === 1) {
        countdownText += " " + seconds + " second";
      } else {
        countdownText += " " + seconds + " seconds";
      }
    }

    return countdownText;
  }

  private calculateTimeRemaining = async () => {
    const maintenanceDateTime = this.props.maintenanceTime;

    if (maintenanceDateTime) {
      // only overwrite the "momentarily" text if a maintenance date and time are present
      const formattedMaintenanceDateTime = new Date(maintenanceDateTime).getTime();
      const now: number = new Date().getTime();
      const timeRemainingInMilliseconds: number = formattedMaintenanceDateTime - now;

      this.setState({
        timeRemainingInMilliseconds,
      });
    }
  }

  private decrementTimeRemaining = () => {
    if (this.state.timeRemainingInMilliseconds !== null) {
      // only need to decrement time and adjust countdown text if a maintenance time and date are present
      if (this.state.timeRemainingInMilliseconds > 0) {
        const timeRemainingInMilliseconds: number = this.state.timeRemainingInMilliseconds - 1000;
        const countdownText: string = this.calculateCountdown(timeRemainingInMilliseconds);

        this.setState({
          countdownText,
          timeRemainingInMilliseconds,
        });
      } else {
        clearInterval(this.timer);

        const countdownText = "now";

        this.setState({
          countdownText,
        });
      }
    }
  }
}

export { MaintenanceAlert };
